// Picks the instrument for a graphic: the bloods slip, the bedside monitor, the rhythm strip or the 12-lead.
// A new graphic always mounts a fresh instrument, so a trace or a count never carries over between questions.
import { useMemo } from 'react';
import type { Graphic } from '@shared/types';
import { BloodsSlip, type SlipPick } from './BloodsSlip';
import { ObsMonitor } from './ObsMonitor';
import { EcgStrip } from './EcgStrip';
import { EcgPaperCard } from './ecg/EcgPaper';
import { monitorRhythm } from './monitor/rhythm';

export interface GraphicCardProps {
  graphic: Graphic;
  /** The answer has been revealed: pen marks on the slip, no printing animation. */
  revealed?: boolean;
  /** The question's deadline, shown when the 12-lead is enlarged. */
  deadlineAt?: number;
  /** Tap the culprit: makes the bloods rows tappable. */
  pick?: SlipPick;
  /**
   * The question's text. The bedside monitor draws the rhythm it states (AF or VT), else sinus at the card's rate; a blood gas
   * prints the sample type and oxygen it states where it shows the gas.
   */
  stem?: string;
  /** The 12-lead opens full screen on a tap. Off unless asked for (the phone's answer area asks; the stage, where nobody taps, does not). */
  enlargeable?: boolean;
}

export function GraphicCard({ graphic, revealed, deadlineAt, pick, stem, enlargeable = false }: GraphicCardProps) {
  const key = useMemo(() => JSON.stringify(graphic), [graphic]);
  if (graphic.type === 'bloods') return <BloodsSlip key={key} graphic={graphic} revealed={revealed} pick={pick} stem={stem} />;
  if (graphic.type === 'ecg') {
    return graphic.view === '12lead'
      ? <EcgPaperCard key={key} graphic={graphic} deadlineAt={deadlineAt} enlargeable={enlargeable} />
      : <EcgStrip key={key} graphic={graphic} />;
  }
  return <ObsMonitor key={key} graphic={graphic} revealed={revealed} rhythm={monitorRhythm(graphic, stem)} />;
}

export { BloodsSlip, ObsMonitor, EcgStrip, EcgPaperCard };
export type { SlipPick };
